import { c as _c } from "react/compiler-runtime";
import { TargetSelector, TargetSelectorUi, type TargetSelectorMode, type TargetSelectorTarget } from '../TargetSelector';
import { StrikeWeightLevel } from '@/lib/piano/touch-design/hammer-weight-level';
import { SmoothStrategy } from '@/lib/geometry/curve-smoother/smooth-strategy.enum';
import { StrikeWeightChart } from '../../charts/StrikeWeightChart';
import { StrikeWeightDesignMode, type StrikeWeightDesignTarget } from '@/lib/piano/touch-design/design/strike-weight-design';
import { type StrikeWeightDesignProps } from './StrikeWeightDesign.types';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { HourglassIcon } from 'lucide-react';
import { DesignLayout } from '../DesignLayout';
import { formatGrams, formatSignedGrams } from '@/lib/format/format-weight';
import { useFrontWeightDesign } from '@/hooks/store/use-design-store';
import { useStrikeWeightTargetSelector } from './hooks/use-strike-weight-target-selector';
import { useStrikeWeightRecommendation } from './hooks/use-strike-weight-recommendation';
import { useTranslation } from '@/hooks/use-translation';
import { useCallback, useMemo } from 'react';
import { FrontWeightDesignMode } from '@/lib/piano/touch-design/design/front-weight-design';
import { useTargetSeries } from '../hooks/use-target-series';
import { useTargetReading } from '../hooks/use-target-reading';
import { benchAdjustment } from '@/lib/piano/touch-design/bench-adjustment';
import { NotEnoughData } from '../NotEnoughData';
const standardCurveTargets: TargetSelectorTarget<StrikeWeightLevel>[] = Object.values(StrikeWeightLevel).map(level => ({
  label: level,
  value: level
}));
export const StrikeWeightDesign: React.FC<StrikeWeightDesignProps> = t0 => {
  const $ = _c(80);
  const {
    analyzedKeyboard,
    requiredDataPercentage,
    notEnoughDataErrorTitle,
    notEnoughDataErrorDescription
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    strikeWeightDesignMode,
    strikeWeightDesignTarget,
    onModeChange,
    onTargetChange,
    updateStrikeWeightDesign
  } = useStrikeWeightTargetSelector();
  useStrikeWeightRecommendation(strikeWeightDesignMode, strikeWeightDesignTarget, updateStrikeWeightDesign);
  const {
    strikeWeightTargetSerie: targetSerie
  } = useTargetSeries();
  let t1;
  if ($[0] !== analyzedKeyboard || $[1] !== targetSerie) {
    t1 = {
      difference: benchAdjustment,
      format: formatGrams,
      formatDifference: formatSignedGrams,
      keyboard: analyzedKeyboard,
      targetSerie
    };
    $[0] = analyzedKeyboard;
    $[1] = targetSerie;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const renderReading = useTargetReading(t1);
  const {
    frontWeightDesignMode
  } = useFrontWeightDesign();
  let t2;
  if ($[3] !== t) {
    t2 = value => t("{{value}} g", {
      value
    });
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const balanceWeightLabelFormatter = t2;
  const shouldDisableComputedMode = frontWeightDesignMode === FrontWeightDesignMode.Computed;
  let t3;
  if ($[5] !== shouldDisableComputedMode || $[6] !== t) {
    t3 = shouldDisableComputedMode ? t("The automatic computation is already active on the front weight design.") : undefined;
    $[5] = shouldDisableComputedMode;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const reason = t3;
  let t4;
  if ($[8] !== reason || $[9] !== shouldDisableComputedMode) {
    t4 = {
      computedModeDisabledReason: reason,
      shouldDisableComputedMode
    };
    $[8] = reason;
    $[9] = shouldDisableComputedMode;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    shouldDisableComputedMode: shouldDisableComputedMode_0,
    computedModeDisabledReason
  } = t4;
  let t5;
  if ($[11] !== analyzedKeyboard) {
    t5 = analyzedKeyboard.mapToArray(_temp).filter(_temp2);
    $[11] = analyzedKeyboard;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const hasEnoughData = t5.length >= Math.round(analyzedKeyboard.size * requiredDataPercentage);
  let t6;
  if ($[13] !== targetSerie?.data) {
    t6 = targetSerie?.data?.some(_temp3);
    $[13] = targetSerie?.data;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const hasEnoughOutputData = t6;
  const shouldDisplayComputeModeWaitingMessage = strikeWeightDesignMode === StrikeWeightDesignMode.Computed && !hasEnoughOutputData;
  let t7;
  if ($[15] !== t) {
    t7 = t("Use strike weight measured values");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t) {
    t8 = t("Keep the strike weight as is.");
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      selectorUi: TargetSelectorUi.UniqueTargetSelector,
      target: null
    };
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t7 || $[21] !== t8) {
    t10 = {
      description: t7,
      label: t8,
      options: t9,
      value: StrikeWeightDesignMode.AsMeasured
    };
    $[20] = t7;
    $[21] = t8;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t) {
    t11 = t("The strike weight will be computed automatically in order to achieve the specified balance weight.");
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t) {
    t12 = t("Uses the springs, the front weight and the ratio set in steps 1, 2 and 4.");
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t) {
    t13 = t("Compute automatically");
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t) {
    t14 = t("Target Balance Weight");
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== balanceWeightLabelFormatter || $[32] !== t14) {
    t15 = {
      labelFormatter: balanceWeightLabelFormatter,
      maxValue: 60,
      minValue: 30,
      placeholder: t14,
      selectorUi: TargetSelectorUi.NumericSelector,
      step: 1
    };
    $[31] = balanceWeightLabelFormatter;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== computedModeDisabledReason || $[35] !== shouldDisableComputedMode_0 || $[36] !== t11 || $[37] !== t12 || $[38] !== t13 || $[39] !== t15) {
    t16 = {
      description: t11,
      disabledReason: computedModeDisabledReason,
      formula: "SW = (BW + FW + BWS \u2212 WBW) / R",
      hint: t12,
      isDisabled: shouldDisableComputedMode_0,
      label: t13,
      options: t15,
      value: StrikeWeightDesignMode.Computed
    };
    $[34] = computedModeDisabledReason;
    $[35] = shouldDisableComputedMode_0;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t) {
    t17 = t("Choose a target from the standard strike weight curves.");
    $[41] = t;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t) {
    t18 = t("Use standard curves");
    $[43] = t;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t) {
    t19 = t("Select your target curve");
    $[45] = t;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t19) {
    t20 = {
      placeholder: t19,
      selectorUi: TargetSelectorUi.ValueSelector,
      targets: standardCurveTargets
    };
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t17 || $[50] !== t18 || $[51] !== t20) {
    t21 = {
      description: t17,
      label: t18,
      options: t20,
      value: StrikeWeightDesignMode.StandardCurves
    };
    $[49] = t17;
    $[50] = t18;
    $[51] = t20;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t) {
    t22 = t("Create a custom smoothed curve based on your measured strike weight data.");
    $[53] = t;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t) {
    t23 = t("Generate a smoothed curve");
    $[55] = t;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      selectorUi: TargetSelectorUi.UniqueTargetSelector,
      target: SmoothStrategy.LeastSquaresRegression
    };
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t22 || $[59] !== t23) {
    t25 = {
      description: t22,
      label: t23,
      options: t24,
      value: StrikeWeightDesignMode.Smoothed
    };
    $[58] = t22;
    $[59] = t23;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== t10 || $[62] !== t16 || $[63] !== t21 || $[64] !== t25) {
    t26 = [t10, t16, t21, t25];
    $[61] = t10;
    $[62] = t16;
    $[63] = t21;
    $[64] = t25;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  const targetSelectorModes = t26;
  let t27;
  if ($[66] !== analyzedKeyboard || $[67] !== hasEnoughData || $[68] !== notEnoughDataErrorDescription || $[69] !== notEnoughDataErrorTitle || $[70] !== onModeChange || $[71] !== onTargetChange || $[72] !== renderReading || $[73] !== shouldDisplayComputeModeWaitingMessage || $[74] !== strikeWeightDesignMode || $[75] !== strikeWeightDesignTarget || $[76] !== t || $[77] !== targetSelectorModes || $[78] !== targetSerie) {
    t27 = !hasEnoughData ? <NotEnoughData title={notEnoughDataErrorTitle} description={notEnoughDataErrorDescription} /> : <DesignLayout objective={<TargetSelector title={t("Target")} modes={targetSelectorModes} selectedMode={strikeWeightDesignMode} selectedTarget={strikeWeightDesignTarget} onModeChange={onModeChange} onTargetChange={onTargetChange} />} chart={shouldDisplayComputeModeWaitingMessage ? <Alert variant="default"><HourglassIcon /><AlertTitle>{t("Waiting for remaining design targets to be specified.")}</AlertTitle><AlertDescription>{t("The strike weight will be computed when all other design targets will be specified.")}</AlertDescription></Alert> : <StrikeWeightChart renderReading={renderReading} variant="design" keyboard={analyzedKeyboard} targetSerie={targetSerie ?? undefined} extraLegendItems={targetSerie ? [{
      colorClassName: "text-ptl-series-target",
      id: "target",
      label: t("Target"),
      mark: "line-thick" as const
    }] : undefined} />} />;
    $[66] = analyzedKeyboard;
    $[67] = hasEnoughData;
    $[68] = notEnoughDataErrorDescription;
    $[69] = notEnoughDataErrorTitle;
    $[70] = onModeChange;
    $[71] = onTargetChange;
    $[72] = renderReading;
    $[73] = shouldDisplayComputeModeWaitingMessage;
    $[74] = strikeWeightDesignMode;
    $[75] = strikeWeightDesignTarget;
    $[76] = t;
    $[77] = targetSelectorModes;
    $[78] = targetSerie;
    $[79] = t27;
  } else {
    t27 = $[79];
  }
  return t27;
};
function _temp(key) {
  return key.payload.strikeWeight;
}
function _temp2(v) {
  return v !== undefined && v !== null;
}
function _temp3(key_0) {
  return key_0?.payload;
}